import React, { useState } from 'react';
import { Contract, ClauseAnalysis } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

interface RiskPanelProps {
    activeContract: Contract | null;
    selectedClauseIndex: number | null;
    setSelectedClauseIndex: (index: number | null) => void;
    onDownload: () => void;
    onInsertRevision?: (clause: ClauseAnalysis, index: number) => void;
    onScrollToClause?: (index: number) => void;
    isAnalyzing?: boolean;
    analysisProgress?: number;
}

export const RiskPanel: React.FC<RiskPanelProps> = ({
    activeContract,
    selectedClauseIndex,
    setSelectedClauseIndex,
    onDownload,
    onInsertRevision,
    onScrollToClause,
    isAnalyzing = false,
    analysisProgress = 0
}) => {
    const { t } = useLanguage();
    const [isSuggestionsExpanded, setIsSuggestionsExpanded] = useState(true);
    const [selectedRiskLevel, setSelectedRiskLevel] = useState<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null>(null);

    if (!activeContract) {
        return (
            <div className="h-full flex items-center justify-center" style={{ 
                background: 'rgba(235, 245, 255, 0.18)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)'
            }}>
                <p className="text-slate-400">{t('risk.empty')}</p>
            </div>
        );
    }

    const clauses = activeContract.clauses || [];

    // 统计风险数量（四个等级）
    const riskCounts = {
        critical: clauses.filter(c => c.riskLevel === 'CRITICAL').length,
        high: clauses.filter(c => c.riskLevel === 'HIGH').length,
        medium: clauses.filter(c => c.riskLevel === 'MEDIUM').length,
        low: clauses.filter(c => c.riskLevel === 'LOW').length
    };

    // 根据选中的风险级别过滤建议
    const allSuggestions = clauses.map((clause, idx) => ({ ...clause, index: idx }));
    const suggestions = selectedRiskLevel
        ? allSuggestions.filter(c => c.riskLevel === selectedRiskLevel)
        : allSuggestions; // 如果没选中，显示全部

    return (
        <div
            className="h-full flex flex-col overflow-hidden"
            style={{
                background: 'rgba(235, 245, 255, 0.18)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)'
            }}
        >
            {/* Scrollable Content - 垂直居中 */}
            <div className="flex-1 overflow-y-auto p-6 pb-3 custom-scrollbar flex items-center justify-center">
                <div className="w-full max-w-md">
                    {/* Risk Summary Card */}
                    <div
                        className="mb-6 p-5 rounded-2xl"
                        style={{
                            background: 'rgba(207, 211, 216, 0.1)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                        }}
                    >
                        <h3 className="text-slate-800 font-bold text-sm mb-4">{t('risk.summary')}</h3>
                        <div className="grid grid-cols-4 gap-2">
                            {/* Critical Risks */}
                            <button
                                onClick={() => setSelectedRiskLevel(selectedRiskLevel === 'CRITICAL' ? null : 'CRITICAL')}
                                className={`p-3 rounded-xl text-center transition-all duration-200 ${selectedRiskLevel === 'CRITICAL' ? 'ring-2 ring-red-400 ring-offset-2 ring-offset-slate-800' : ''
                                    }`}
                                style={{
                                    background: selectedRiskLevel === 'CRITICAL'
                                        ? 'rgba(239, 68, 68, 0.2)'
                                        : 'rgba(239, 68, 68, 0.15)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    boxShadow: selectedRiskLevel === 'CRITICAL'
                                        ? '0 4px 12px rgba(239, 68, 68, 0.2)'
                                        : '0 2px 6px rgba(239, 68, 68, 0.1)',
                                    transform: selectedRiskLevel === 'CRITICAL' ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <p className="text-red-700 text-[10px] font-semibold mb-1">{t('risk.criticalRisks')}</p>
                                <p className="text-red-800 text-xl font-bold">{riskCounts.critical}</p>
                            </button>
                            {/* High Risks */}
                            <button
                                onClick={() => setSelectedRiskLevel(selectedRiskLevel === 'HIGH' ? null : 'HIGH')}
                                className={`p-3 rounded-xl text-center transition-all duration-200 ${selectedRiskLevel === 'HIGH' ? 'ring-2 ring-orange-400 ring-offset-2 ring-offset-slate-800' : ''
                                    }`}
                                style={{
                                    background: selectedRiskLevel === 'HIGH'
                                        ? 'rgba(249, 115, 22, 0.2)'
                                        : 'rgba(249, 115, 22, 0.15)',
                                    border: '1px solid rgba(249, 115, 22, 0.3)',
                                    boxShadow: selectedRiskLevel === 'HIGH'
                                        ? '0 4px 12px rgba(249, 115, 22, 0.2)'
                                        : '0 2px 6px rgba(249, 115, 22, 0.1)',
                                    transform: selectedRiskLevel === 'HIGH' ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <p className="text-orange-700 text-[10px] font-semibold mb-1">{t('risk.highRisks')}</p>
                                <p className="text-orange-800 text-xl font-bold">{riskCounts.high}</p>
                            </button>
                            {/* Medium Risks */}
                            <button
                                onClick={() => setSelectedRiskLevel(selectedRiskLevel === 'MEDIUM' ? null : 'MEDIUM')}
                                className={`p-3 rounded-xl text-center transition-all duration-200 ${selectedRiskLevel === 'MEDIUM' ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-slate-800' : ''
                                    }`}
                                style={{
                                    background: selectedRiskLevel === 'MEDIUM'
                                        ? 'rgba(234, 179, 8, 0.2)'
                                        : 'rgba(234, 179, 8, 0.15)',
                                    border: '1px solid rgba(234, 179, 8, 0.3)',
                                    boxShadow: selectedRiskLevel === 'MEDIUM'
                                        ? '0 4px 12px rgba(234, 179, 8, 0.2)'
                                        : '0 2px 6px rgba(234, 179, 8, 0.1)',
                                    transform: selectedRiskLevel === 'MEDIUM' ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <p className="text-yellow-700 text-[10px] font-semibold mb-1">{t('risk.mediumRisks')}</p>
                                <p className="text-yellow-800 text-xl font-bold">{riskCounts.medium}</p>
                            </button>
                            {/* Low Risks */}
                            <button
                                onClick={() => setSelectedRiskLevel(selectedRiskLevel === 'LOW' ? null : 'LOW')}
                                className={`p-3 rounded-xl text-center transition-all duration-200 ${selectedRiskLevel === 'LOW' ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-slate-800' : ''
                                    }`}
                                style={{
                                    background: selectedRiskLevel === 'LOW'
                                        ? 'rgba(34, 197, 94, 0.2)'
                                        : 'rgba(34, 197, 94, 0.15)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    boxShadow: selectedRiskLevel === 'LOW'
                                        ? '0 4px 12px rgba(34, 197, 94, 0.2)'
                                        : '0 2px 6px rgba(34, 197, 94, 0.1)',
                                    transform: selectedRiskLevel === 'LOW' ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <p className="text-green-700 text-[10px] font-semibold mb-1">{t('risk.lowRisks')}</p>
                                <p className="text-green-800 text-xl font-bold">{riskCounts.low}</p>
                            </button>
                        </div>
                    </div>

                    {/* AI Revision Suggestions Card */}
                    <div
                        className="rounded-2xl"
                        style={{
                            background: 'rgba(207, 211, 216, 0.1)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                        }}
                    >
                        {/* Header */}
                        <div
                            className="p-5 flex items-center justify-between cursor-pointer"
                            onClick={() => setIsSuggestionsExpanded(!isSuggestionsExpanded)}
                        >
                            <h3 className="text-slate-800 font-bold text-sm">{t('risk.aiSuggestions')}</h3>
                            <svg
                                className={`w-4 h-4 text-slate-800 transition-transform duration-300 ${isSuggestionsExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                            </svg>
                        </div>

                        {/* Suggestions List */}
                        {isSuggestionsExpanded && (
                            <div className="px-5 pb-5 space-y-2 h-80 overflow-y-auto custom-scrollbar">
                                {isAnalyzing ? (
                                    <div className="h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent mx-auto mb-3"></div>
                                            <p className="text-slate-600 text-sm">{t('config.analyzing')}</p>
                                        </div>
                                    </div>
                                ) : suggestions.length === 0 ? (
                                    <div className="h-full flex items-center justify-center">
                                        <p className="text-slate-600 text-sm text-center">
                                            {selectedRiskLevel
                                                ? `${t('risk.allRisks')}${selectedRiskLevel === 'CRITICAL' ? t('risk.criticalRisks') : selectedRiskLevel === 'HIGH' ? t('risk.highRisks') : selectedRiskLevel === 'MEDIUM' ? t('risk.mediumRisks') : t('risk.lowRisks')}${t('risk.noRisks')}`
                                                : t('risk.empty')}
                                        </p>
                                    </div>
                                ) : (
                                    suggestions.map((clause, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => {
                                                setSelectedClauseIndex(clause.index);
                                                // 触发滚动到对应条款
                                                if (onScrollToClause) {
                                                    setTimeout(() => {
                                                        onScrollToClause(clause.index);
                                                    }, 100);
                                                }
                                            }}
                                            className="w-full p-4 rounded-xl text-left transition-all duration-200 hover:opacity-90"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.95)',
                                                backdropFilter: 'blur(30px) saturate(120%)',
                                                WebkitBackdropFilter: 'blur(30px) saturate(120%)',
                                                border: '1px solid rgba(255, 255, 255, 0.5)',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                                            }}
                                        >
                                            {/* 风险级别标签 */}
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-xs font-bold px-2 py-1 rounded ${clause.riskLevel === 'CRITICAL'
                                                        ? 'bg-red-500/20 text-red-700 border border-red-500/30'
                                                        : clause.riskLevel === 'HIGH'
                                                            ? 'bg-orange-500/20 text-orange-700 border border-orange-500/30'
                                                            : clause.riskLevel === 'MEDIUM'
                                                                ? 'bg-yellow-500/20 text-yellow-700 border border-yellow-500/30'
                                                                : 'bg-green-500/20 text-green-700 border border-green-500/30'
                                                    }`}>
                                                    {clause.riskLevel === 'CRITICAL' ? t('risk.level.critical') :
                                                        clause.riskLevel === 'HIGH' ? t('risk.level.high') :
                                                            clause.riskLevel === 'MEDIUM' ? t('risk.level.medium') : t('risk.level.low')}
                                                </span>
                                                <span className="text-slate-600 text-xs">
                                                    {t('risk.clause')} {clause.index + 1}
                                                </span>
                                            </div>

                                            {/* 修订建议 */}
                                            {clause.suggestedAction && (
                                                <div className="mt-2">
                                                    <p className="text-blue-700 text-xs font-semibold mb-1">{t('risk.suggestedActionTitle')}</p>
                                                    <p
                                                        className="text-slate-700 text-xs leading-relaxed"
                                                        style={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {clause.suggestedAction.replace(/\\n/g, '\n')}
                                                    </p>
                                                </div>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                /* 滚动条 */
                .custom-scrollbar::-webkit-scrollbar { 
                    width: 6px; 
                }
                .custom-scrollbar::-webkit-scrollbar-track { 
                    background: rgba(30, 41, 59, 0.5); 
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb { 
                    background: rgba(148, 163, 184, 0.4); 
                    border-radius: 10px; 
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
                    background: rgba(148, 163, 184, 0.6); 
                }
            `}</style>
        </div>
    );
};
